/**
 * Copyright © Smartex Canada All rights reserved.
 * See LICENSE for license details.
 */

type TempCookie = {
    value: string
    days?: number
    skipSetDomain?: boolean
};

export type CookieConfig = {
    expires?: null
    path?: string
    domain?: string | null
    secure?: boolean
    lifetime?: number | null
    samesite?: string
    cookie_restriction_enabled?: boolean
};

const defaultCookieConfig = {
    expires: null,
    path: '/',
    domain: null,
    secure: false,
    lifetime: null,
    samesite: 'lax'
};

const sessionCookieMarker = {
    noLifetime: true
};
type SessionCookieMarker = typeof sessionCookieMarker;

function lifetimeToExpires(options: CookieConfig) {
    const lifetime = options.lifetime || defaultCookieConfig.lifetime;
    if (lifetime) {
        const date = new Date();
        date.setTime(date.getTime() + 1000 * lifetime);
        return date;
    }
    return null;
}

const cookieTempStorage: Record<string, TempCookie> = {};

const cookieManager = {
    get(name: string) {
        const cookie = document.cookie.match('(^|;) ?' + name + '=([^;]*)(;|$)');
        return cookie ? cookie[2] : null;
    },

    set(name: string, value: string, days?: number | SessionCookieMarker, skipSetDomain?: boolean) {
        const config = window.COOKIE_CONFIG || {};

        const expires = days && days !== sessionCookieMarker
            ? lifetimeToExpires({ lifetime: 86400 * (days as number), expires: null})
            : lifetimeToExpires(config) || defaultCookieConfig.expires;

        const path = config.path || defaultCookieConfig.path;
        const domain = !skipSetDomain && (config.domain || defaultCookieConfig.domain);
        const secure = config.secure || defaultCookieConfig.secure;
        const samesite = config.samesite || defaultCookieConfig.samesite;

        document.cookie = name + '=' + encodeURIComponent(value)
            + (expires ? '; expires=' + expires.toUTCString() :  '')
            + (path ? '; path=' + path : '')
            + (domain ? '; domain=' + domain : '')
            + (secure ? '; secure' : '')
            + '; samesite=' + (samesite || 'lax');
    },

    isCookieAllowed(name: string): boolean {
        const group = this.getGroupByCookieName(name);
        return group ? window.cookie_consent_groups[group] : this.isAllowedToSaveCookie();
    },

    getGroupByCookieName(name: string) {
        const config = window.cookie_consent_config || {};

        return Object.keys(config).find(prop => config[prop].includes(name)) || null;
    },

    isAllowedToSaveCookie(): boolean {
        const allowed = this.get('user_allowed_save_cookie');
        if (allowed) {
            const allowedWebsites = JSON.parse(decodeURIComponent(allowed));

            return allowedWebsites[window.CURRENT_WEBSITE_ID] === 1;
        }
        return false;
    },

    saveTempCookies() {
        Object.keys(cookieTempStorage).forEach(name => {
            if (this.isCookieAllowed(name)) {
                const data = cookieTempStorage[name];
                this.set(name, data.value, data.days, data.skipSetDomain);
                delete cookieTempStorage[name];
            }
        });
    }
};

/**
 * Retrieve cookie value by name
 */
export function getCookie(name: string): string | null {
    const config = window.COOKIE_CONFIG || {};

    if (config.cookie_restriction_enabled && !cookieManager.isCookieAllowed(name)) {
        return cookieTempStorage[name] ? cookieTempStorage[name].value : null;
    }

    return cookieManager.get(name);
}

/**
 * Set cookie value by name
 */
export function setCookie(name: string, value: string, days?: number, skipSetDomain?: boolean) {
    const config = window.COOKIE_CONFIG || {};

    if (config.cookie_restriction_enabled && !cookieManager.isCookieAllowed(name)) {
        cookieTempStorage[name] = { value, days, skipSetDomain };
        return;
    }

    cookieManager.set(name, value, days, skipSetDomain);
}

window.addEventListener('user-allowed-save-cookies', () => cookieManager.saveTempCookies());
